import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import Svg, {
  Path,
  Defs,
  ClipPath as SvgClipPath,
  G,
  Rect,
} from 'react-native-svg';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { useSobriety } from '@/contexts/SobrietyContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  subDays,
  differenceInYears,
  differenceInMonths,
  differenceInDays,
  addYears,
  addMonths,
  format,
} from 'date-fns';
import { ptBR, enUS, es } from 'date-fns/locale';
import { calculateSobrietyDays } from '@/utils/sobrietyDays';
import TimeSquareIcon from './icons/TimeSquareIcon';
import EmptyState from './EmptyState';

interface TimeBreakdown {
  years: number;
  months: number;
  days: number;
  hours: number;
  totalCompletedDays: number;
}

function calculateTimeBreakdown(startDate: Date, baseDays: number, now: Date): TimeBreakdown {
  const effectiveStart = subDays(startDate, baseDays);

  if (now < effectiveStart) {
    return { years: 0, months: 0, days: 0, hours: 0, totalCompletedDays: 0 };
  }

  let cursor = effectiveStart;
  const years = differenceInYears(now, cursor);
  cursor = addYears(cursor, years);
  const months = differenceInMonths(now, cursor);
  cursor = addMonths(cursor, months);
  const days = differenceInDays(now, cursor);

  const totalMilliseconds = now.getTime() - effectiveStart.getTime();
  const totalHours = Math.floor(totalMilliseconds / (1000 * 60 * 60));
  const remainingMinutes = Math.floor((totalMilliseconds % (1000 * 60 * 60)) / (1000 * 60));
  const hours = (totalHours + remainingMinutes / 60) % 24; // Fractional hours within the current day cycle
  // Day count comes from the single source of truth so it matches every other
  // surface (Missões, widget, share, Bill) — see utils/sobrietyDays.ts.
  const totalCompletedDays = calculateSobrietyDays(startDate, baseDays, now);

  return {
    years: Math.max(0, years),
    months: Math.max(0, months),
    days: Math.max(0, days),
    hours: Math.max(0, hours),
    totalCompletedDays,
  };
}

const MIN_BAR_HEIGHT = 8;

function computeBarHeights(
  breakdown: TimeBreakdown,
  containerHeight: number,
): { anos: number; meses: number; dias: number; horas: number } {
  const h = containerHeight > 0 ? containerHeight : 294;

  const ratioAno = breakdown.years / 10;
  const ratioMes = breakdown.months / 12;
  const ratioDia = breakdown.days / 30;
  const ratioHora = breakdown.hours / 24;

  const maxRatio = Math.max(ratioAno, ratioMes, ratioDia, ratioHora);

  if (maxRatio === 0) {
    return { anos: MIN_BAR_HEIGHT, meses: MIN_BAR_HEIGHT, dias: MIN_BAR_HEIGHT, horas: MIN_BAR_HEIGHT };
  }

  // Scale so the tallest bar fills 88 % of the container
  const scaledMax = h * 0.88;

  const calc = (ratio: number): number => {
    if (ratio === 0) return MIN_BAR_HEIGHT;
    return Math.max(MIN_BAR_HEIGHT, (ratio / maxRatio) * scaledMax);
  };

  return {
    anos: calc(ratioAno),
    meses: calc(ratioMes),
    dias: calc(ratioDia),
    horas: calc(ratioHora),
  };
}

function HorasSvgDecoration() {
  return (
    <Svg width="85" height="180" viewBox="0 0 85 180" fill="none">
      <Defs>
        <SvgClipPath id="horasClip">
          <Path d="M0 42.5C0 24.6638 0 15.7458 4.59495 9.52658C5.98414 7.64635 7.64635 5.98414 9.52658 4.59495C15.7458 0 24.6638 0 42.5 0C60.3362 0 69.2542 0 75.4734 4.59495C77.3537 5.98414 79.0159 7.64635 80.4051 9.52658C85 15.7458 85 24.6638 85 42.5V137.5C85 155.336 85 164.254 80.4051 170.473C79.0159 172.354 77.3537 174.016 75.4734 175.405C69.2542 180 60.3362 180 42.5 180C24.6638 180 15.7458 180 9.52658 175.405C7.64635 174.016 5.98414 172.354 4.59495 170.473C0 164.254 0 155.336 0 137.5V42.5Z" />
        </SvgClipPath>
      </Defs>
      <G clipPath="url(#horasClip)">
        <Rect x="0" y="0" width="85" height="180" fill="#669AB7" />
      </G>
    </Svg>
  );
}

export default function SobrietyClockFull() {
  const router = useRouter();
  const { sobrietyState } = useSobriety();
  const { t, language } = useLanguage();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [communityCount, setCommunityCount] = useState<number>(0);
  const [barsContainerHeight, setBarsContainerHeight] = useState(0);

  const isSetup = sobrietyState.isSetup;

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const apiUrl = 'https://mobile.padrinho.app';
        const resp = await fetch(`${apiUrl}/api/stats/users`);
        if (resp.ok) {
          const data = await resp.json();
          const count = Number(data?.count);
          if (data?.success && !isNaN(count) && count > 0) {
            setCommunityCount(count);
          }
        }
      } catch (err) {
        console.log('[SobrietyClockFull] Community count fetch error:', err);
      }
    };
    fetchCount();
  }, []);

  const breakdown = useMemo(() => {
    if (!isSetup || !sobrietyState.currentRecord) return null;
    const { start_date, base_days } = sobrietyState.currentRecord;
    return calculateTimeBreakdown(new Date(start_date), base_days, currentTime);
  }, [isSetup, sobrietyState.currentRecord, currentTime]);

  const formattedDate = useMemo(() => {
    const locale = language === 'pt' ? ptBR : language === 'es' ? es : enUS;
    const todayLabel = t('diary.today') || 'Hoje';
    const dateStr = language === 'en'
      ? format(currentTime, 'MMMM d', { locale })
      : format(currentTime, "d 'de' MMMM", { locale });
    return `${todayLabel}, ${dateStr}`;
  }, [currentTime, language, t]);

  const barHeights = useMemo(() => {
    if (!breakdown) return { anos: MIN_BAR_HEIGHT, meses: MIN_BAR_HEIGHT, dias: MIN_BAR_HEIGHT, horas: MIN_BAR_HEIGHT };
    return computeBarHeights(breakdown, barsContainerHeight);
  }, [breakdown, barsContainerHeight]);

  const anosLabel = t('diary.clock.anos').toLowerCase();
  const mesesLabel = t('diary.clock.meses').toLowerCase();
  const diasLabel = t('diary.clock.dias').toLowerCase();
  const horasLabel = t('diary.clock.horas').toLowerCase();

  const handleShare = () => {
    router.push('/share-sobriety' as any);
  };

  const handleStart = () => router.push('/sobriety-clock' as any);
  const handleRestart = () => router.push('/sobriety-clock' as any);
  const handleEdit = () => router.push('/sobriety-clock' as any);

  if (!isSetup || !breakdown) {
    // Canonical EmptyState usage — see components/EmptyState.tsx and
    // documentation/01. Platform/00. DESIGN SYSTEM/02. COMPONENT_MAP.md
    // (Mobile — Padrinho App > EmptyState). Other empty surfaces in
    // the app should match this shape.
    return (
      <EmptyState
        icon={<TimeSquareIcon color="#151515" size={16} />}
        eyebrow={formattedDate}
        title={t('diary.clock.emptyTitle')}
        action={{ label: t('diary.clock.startButton'), onPress: handleStart }}
      />
    );
  }

  return (
    <View style={styles.activeWrapper}>
      {/* Top info section — icon + date + community */}
      <View style={styles.topGroup}>
        <View style={styles.iconBox}>
          <View style={styles.iconSquare}>
            <TimeSquareIcon color="#151515" size={16} />
          </View>
        </View>
        <Text style={styles.dateText}>{formattedDate}</Text>
        {communityCount > 0 && (
          <Text style={styles.communityText}>
            {t('diary.clock.communityCount', { count: String(communityCount) })}
          </Text>
        )}
      </View>

      {/* Bars section */}
      <View
        style={styles.barsRow}
        onLayout={(e) => setBarsContainerHeight(e.nativeEvent.layout.height)}
      >
        {/* Anos */}
        <View style={[styles.barCard, { height: barHeights.anos }]}>
          <LinearGradient
            colors={['#C0D1DB', 'rgba(192,209,219,0)']}
            start={{ x: 0.5, y: 0 }}
            end={{ x: 0.5, y: 1 }}
            style={StyleSheet.absoluteFillObject}
          />
          <Text style={styles.barLabel}>{`${breakdown.years} ${anosLabel}`}</Text>
        </View>

        {/* Meses */}
        <View style={[styles.barCard, { height: barHeights.meses }]}>
          <LinearGradient
            colors={['#C0D1DB', 'rgba(192,209,219,0)']}
            start={{ x: 0.5, y: 0 }}
            end={{ x: 0.5, y: 1 }}
            style={StyleSheet.absoluteFillObject}
          />
          <Text style={styles.barLabel}>{`${breakdown.months} ${mesesLabel}`}</Text>
        </View>

        {/* Dias */}
        <View style={[styles.barCard, { height: barHeights.dias }]}>
          <LinearGradient
            colors={['#C0D1DB', 'rgba(192,209,219,0)']}
            start={{ x: 0.5, y: 0 }}
            end={{ x: 0.5, y: 1 }}
            style={StyleSheet.absoluteFillObject}
          />
          <Text style={styles.barLabel}>{`${breakdown.days} ${diasLabel}`}</Text>
        </View>

        {/* Horas */}
        <View style={[styles.barCard, styles.horasCard, { height: barHeights.horas }]}>
          {/* SVG is absolute — painted first; normal-flow text renders on top */}
          <View style={styles.horasSvgWrapper}>
            <HorasSvgDecoration />
          </View>
          <Text style={[styles.barLabel, styles.horasBarLabel]}>{`${Math.floor(breakdown.hours)} ${horasLabel}`}</Text>
        </View>
      </View>

      {/* Action buttons */}
      <View style={styles.actionRow}>
        {/* Compartilhar */}
        <TouchableOpacity style={[styles.actionButton, styles.darkButton]} onPress={handleShare} activeOpacity={0.8}>
          <View style={styles.btnIconWrapper}>
            <Svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <Path
                d="M7.22571 5.06738L4.92773 7.36536"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <Path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.92747 7.36533L0.384136 4.58733C-0.211198 4.22333 -0.090531 3.32533 0.580136 3.13133L11.3068 0.0326645C11.9168 -0.144002 12.4808 0.425331 12.2981 1.03333L9.11547 11.676C8.91614 12.3427 8.02214 12.4573 7.6608 11.8627L4.92747 7.36533Z"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Svg>
          </View>
          <Text style={styles.darkButtonText}>{t('diary.clock.share')}</Text>
        </TouchableOpacity>

        {/* Recomeçar */}
        <TouchableOpacity style={[styles.actionButton, styles.lightButton]} onPress={handleRestart} activeOpacity={0.8}>
          <View style={styles.btnIconWrapper}>
            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <Path d="M9.82627 9.27716L7.96094 8.1629V5.75537" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <Path d="M3.18555 4.42266C3.46573 4.04066 3.79067 3.69431 4.15322 3.38818" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <Path d="M11.7082 12.7241C10.804 13.4331 9.68783 13.8909 8.45616 13.9831C7.84976 14.0285 7.25763 13.9805 6.69336 13.854" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <Path d="M4.56763 12.9074C3.66351 12.2822 2.94034 11.4118 2.48828 10.3857" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <Path d="M2.21907 6.36475C2.04395 7.00683 1.96807 7.68589 2.01996 8.38703" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <Path d="M13.095 11.1488C13.7014 10.1701 14.0393 9.0124 13.992 7.77553C13.8642 4.46066 11.0747 1.87737 7.75977 2.0045" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </Svg>
          </View>
          <Text style={styles.lightButtonText}>{t('diary.clock.restart')}</Text>
        </TouchableOpacity>

        {/* Editar */}
        <TouchableOpacity style={[styles.actionButton, styles.lightButton]} onPress={handleEdit} activeOpacity={0.8}>
          <View style={styles.btnIconWrapper}>
            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <Path
                d="M5.42214 13.2816L6.53317 12.9584C7.13035 12.7844 7.65061 12.4126 8.00788 11.9044L12.2671 5.99826C12.7769 5.29872 12.6231 4.3191 11.9236 3.80921L10.7679 2.96669C10.0685 2.45766 9.08801 2.61161 8.57888 3.31018L4.27293 9.28244C3.94433 9.75044 3.7707 10.3093 3.77549 10.8814L3.78617 12.0653C3.79423 12.9125 4.60883 13.518 5.42214 13.2816Z"
                stroke="#151515"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Svg>
          </View>
          <Text style={styles.lightButtonText}>{t('diary.clock.edit')}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  activeWrapper: {
    flex: 1,
    paddingHorizontal: 24,
    paddingBottom: 24,
    gap: 16,
    flexDirection: 'column',
  },
  // Empty-state styles (emptyWrapper / emptyGroup / emptyTextGroup /
  // emptyTitle / startButton / startButtonText) were moved into the
  // shared EmptyState component on 2026-04-30 — do not re-add them
  // here, use <EmptyState /> instead.
  topGroup: {
    paddingVertical: 16,
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    flexDirection: 'column',
    flexShrink: 0,
    width: '100%',
  },
  iconBox: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: '#F0EEE4',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  iconSquare: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dateText: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 19.6,
  },
  communityText: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 19.6,
    alignSelf: 'stretch',
  },
  barsRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 2,
  },
  barCard: {
    flex: 1,
    borderRadius: 12,
    overflow: 'hidden',
    paddingTop: 12,
    alignItems: 'center',
    position: 'relative',
  },
  horasCard: {
    backgroundColor: '#669AB7',
  },
  horasSvgWrapper: {
    position: 'absolute',
    bottom: -44,
    left: -0.25,
    width: 85,
    height: 180,
  },
  barLabel: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    lineHeight: 19.6,
  },
  horasBarLabel: {
    color: '#FFFFFF',
  },
  actionRow: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    flex: 1,
    height: 70,
    borderRadius: 12,
    padding: 12,
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  darkButton: {
    backgroundColor: '#151515',
  },
  lightButton: {
    backgroundColor: '#F0EEE4',
  },
  btnIconWrapper: {
    width: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  darkButtonText: {
    fontSize: 12,
    fontFamily: 'Inter_700Bold',
    color: '#FFFFFF',
    lineHeight: 12,
  },
  lightButtonText: {
    fontSize: 12,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    lineHeight: 12,
  },
});
